import {Grid2} from "@mui/material";
import StringInputField from "@/lib/components/inputfields/string-input-field";
import {useModelState} from "@/lib/modelstate";
import {tenantsApi} from "@/boxhub-api";
import * as React from "react";
import {useMemo} from "react";
import * as yup from "yup";
import {useTranslation} from "react-i18next";
import {DialogEx} from "@/lib/components/DialogEx";
import {ButtonEx} from "@/lib/components/buttons/ButtonEx";
import {AdminI18N, CommonI18N} from "@/translations";
import {Constants} from "@/api/constants";

export type CreateTenantDialogProps = {
    open: boolean;
    close: () => void;
};

const useSchema = () => {
    const { t } = useTranslation();
    return useMemo(
        () =>
            yup.object().shape({
                tenantName: yup
                    .string()
                    .required(t(AdminI18N.companies.validation.tenantNameRequired))
                    .min(
                        Constants.TENANT_NAME_LENGTH_MIN,
                        t(AdminI18N.companies.validation.tenantNameMin, { min: Constants.TENANT_NAME_LENGTH_MIN })
                    )
                    .max(
                        Constants.TENANT_NAME_LENGTH_MAX,
                        t(AdminI18N.companies.validation.tenantNameMax, { max: Constants.TENANT_NAME_LENGTH_MAX })
                    )
            }),
        [t]
    );
};

export const CreateTenantDialog = ({ open, close }: CreateTenantDialogProps) => {
    const { t } = useTranslation();
    const schema = useSchema();
    const { model, errors, onChange, save } = useModelState<{ tenantName: string }>({
        initialState: { tenantName: "" },
        schema,
        saver: async (data) => {
            const response = await tenantsApi.initiateTenant({ tenantName: data.tenantName });
            // Discard the TenantDTO payload: the form model must keep its {tenantName} shape.
            return { ...response, data: undefined };
        }
    });

    const buttons = (
        <>
            <Grid2>
                <ButtonEx label={t(CommonI18N.action.cancel)} onClick={close} color="secondary" variant="text" />
            </Grid2>
            <Grid2>
                <ButtonEx
                    label={t(CommonI18N.action.save)}
                    onClick={async () => {
                        if (await save()) {
                            close();
                        }
                    }}
                    data-cy="create-tenant-submit-button"
                />
            </Grid2>
        </>
    );

    return (
        <DialogEx
            open={open}
            onClose={close}
            title={t(AdminI18N.companies.dialog.createTenantTitle)}
            buttons={buttons}
            fullWidth
            maxWidth="md"
        >
            <Grid2 container spacing={2} direction="column" sx={{ pt: 1 }}>
                <Grid2>
                    <StringInputField
                        name="tenantName"
                        label={t(AdminI18N.companies.field.tenantName)}
                        value={model.tenantName}
                        onChange={onChange}
                        error={errors.tenantName}
                    />
                </Grid2>
            </Grid2>
        </DialogEx>
    );
};
